import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { User, Loader2, CheckCircle, Eye, EyeOff, Lock, Shield } from "lucide-react";
import { useAuth } from "@/hooks/useAuth";
import { useUpdatePerfil } from "@/hooks/useUsuarios";
import { useUpdateUsuario } from "@/hooks/useUsuarios";
import { toast } from "sonner";
import { UserRole, getRoleDisplayName, isVendedorScopedRole } from "@/types/auth";
import { supabase } from "@/integrations/supabase/client";
import { validatePassword, getPasswordErrorMessage } from "@/utils/security";

export function EditarPerfilDialog() {
  const [open, setOpen] = useState(false);
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const { profile, refreshProfile } = useAuth();
  const updatePerfil = useUpdatePerfil();
  const updateUsuario = useUpdateUsuario();
  
  const isTi = profile?.role === 'ti';
  const isAdmin = profile?.role === 'admin' || isTi;
  const isOperator = profile?.role === 'operator' || isTi;
  const isVendedor = isVendedorScopedRole(profile?.role);
  const [isUpdatingPassword, setIsUpdatingPassword] = useState(false);
  
  const [formData, setFormData] = useState({
    nome: "",
    email: "",
    telefone: "",
    setor_departamento: undefined as 'vendas' | 'financeiro' | 'tecnico' | 'administrativo' | undefined,
    role: undefined as UserRole | undefined,
    ativo: true,
    // Campos de senha
    senhaAtual: "",
    novaSenha: "",
    confirmarSenha: ""
  });

  // Atualizar formData quando o profile mudar
  useEffect(() => {
    if (profile) {
      setFormData({
        nome: profile.nome || "",
        email: profile.email || "",
        telefone: profile.telefone || "",
        setor_departamento: profile.setor_departamento || undefined,
        role: profile.role || undefined,
        ativo: profile.ativo !== false,
        senhaAtual: "",
        novaSenha: "",
        confirmarSenha: ""
      });
    }
  }, [profile]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!profile?.id) {
      toast.error('ID do perfil não encontrado');
      return;
    }

    // Validação de senha (própria senha)
    const wantsToChangePassword = !!(formData.novaSenha || formData.confirmarSenha);
    if (wantsToChangePassword) {
      if (!formData.senhaAtual) {
        toast.error('Senha atual é obrigatória para alterar a senha');
        return;
      }
      if (formData.novaSenha !== formData.confirmarSenha) {
        toast.error('Nova senha e confirmação não coincidem');
        return;
      }
      if (!validatePassword(formData.novaSenha)) {
        toast.error(getPasswordErrorMessage(formData.novaSenha));
        return;
      }
    }

    // Vendedor: só pode alterar a própria senha (não chama Edge Function; usa apenas supabase.auth)
    if (isVendedor) {
      if (!wantsToChangePassword || !formData.novaSenha || !formData.senhaAtual) {
        toast.info('Preencha senha atual, nova senha e confirmação para alterar sua senha.');
        return;
      }
      setIsUpdatingPassword(true);
      try {
        const email = profile.email;
        if (!email) {
          toast.error('E-mail do perfil não encontrado');
          return;
        }
        const { error: signInError } = await supabase.auth.signInWithPassword({
          email,
          password: formData.senhaAtual,
        });
        if (signInError) {
          toast.error('Senha atual incorreta. Tente novamente.');
          return;
        }
        const { error: updateError } = await supabase.auth.updateUser({
          password: formData.novaSenha,
        });
        if (updateError) {
          toast.error('Erro ao atualizar senha: ' + updateError.message);
          return;
        }
        toast.success('Senha alterada com sucesso!');
        setOpen(false);
        setFormData(prev => ({ ...prev, senhaAtual: "", novaSenha: "", confirmarSenha: "" }));
        if (refreshProfile) refreshProfile();
      } catch (err) {
        console.error('Erro ao alterar senha:', err);
        toast.error('Erro ao alterar senha. Tente novamente.');
      } finally {
        setIsUpdatingPassword(false);
      }
      return;
    }

    try {
      // 1) Atualizar dados básicos do perfil (nome, telefone, setor) — só para não-vendedor
      await updatePerfil.mutateAsync({
        id: profile.id,
        nome: formData.nome,
        telefone: formData.telefone,
        setor_departamento: formData.setor_departamento,
      });

      // 2) Alterar própria senha via Auth (não usa Edge Function)
      if (wantsToChangePassword && formData.novaSenha && formData.senhaAtual) {
        const email = profile.email;
        if (!email) {
          toast.error('E-mail do perfil não encontrado');
          return;
        }
        const { error: signInError } = await supabase.auth.signInWithPassword({
          email,
          password: formData.senhaAtual,
        });
        if (signInError) {
          toast.error('Senha atual incorreta. Tente novamente.');
          return;
        }
        const { error: updateError } = await supabase.auth.updateUser({
          password: formData.novaSenha,
        });
        if (updateError) {
          toast.error('Erro ao atualizar senha: ' + updateError.message);
          return;
        }
        toast.success('Senha alterada com sucesso!');
      }

      // 3) Se admin alterou role ou ativo do próprio perfil
      const roleOuAtivoAlterado = isAdmin && (
        formData.role !== profile.role ||
        formData.ativo !== profile.ativo
      );
      if (roleOuAtivoAlterado) {
        await updateUsuario.mutateAsync({
          id: profile.id,
          nome: formData.nome,
          email: profile.email ?? "",
          role: (formData.role ?? profile.role) as UserRole,
          setor_departamento: formData.setor_departamento,
          telefone: formData.telefone,
          ativo: formData.ativo,
        });
      }

      setOpen(false);
      if (refreshProfile) {
        refreshProfile();
      }
      setFormData(prev => ({
        ...prev,
        senhaAtual: "",
        novaSenha: "",
        confirmarSenha: ""
      }));
      toast.success('Perfil atualizado com sucesso!');
    } catch (error) {
      console.error('Erro ao atualizar perfil:', error);
      toast.error('Erro ao atualizar perfil. Tente novamente.');
    }
  };

  const handleReset = () => {
    if (profile) {
      setFormData({
        nome: profile.nome || "",
        email: profile.email || "",
        telefone: profile.telefone || "",
        setor_departamento: profile.setor_departamento || undefined,
        role: profile.role || undefined,
        ativo: profile.ativo !== false,
        senhaAtual: "",
        novaSenha: "",
        confirmarSenha: ""
      });
    }
    setShowCurrentPassword(false);
    setShowNewPassword(false);
    setShowConfirmPassword(false);
  };

  const handleOpenChange = (newOpen: boolean) => {
    setOpen(newOpen);
    // Resetar o formulário quando abrir
    if (newOpen && profile) {
      setFormData({
        nome: profile.nome || "",
        email: profile.email || "",
        telefone: profile.telefone || "",
        setor_departamento: profile.setor_departamento || undefined,
        role: profile.role || undefined,
        ativo: profile.ativo !== false,
        senhaAtual: "",
        novaSenha: "",
        confirmarSenha: ""
      });
    }
    // Resetar visibilidade das senhas
    setShowCurrentPassword(false);
    setShowNewPassword(false);
    setShowConfirmPassword(false);
  };

  if (!profile) {
    return null;
  }

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogTrigger asChild>
        <Button variant="outline" size="sm" className="flex items-center gap-2">
          <User className="h-4 w-4" />
          Editar Perfil
        </Button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-[600px] max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <User className="h-5 w-5 text-blue-600" />
            Editar Meu Perfil
          </DialogTitle>
        </DialogHeader>
        
        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Informações do Usuário */}
          <div className="space-y-4">
            <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
              <div className="w-10 h-10 bg-green-600 rounded-full flex items-center justify-center">
                <span className="text-white font-bold text-sm">
                  {profile.nome?.charAt(0) || profile.email?.charAt(0) || 'U'}
                </span>
              </div>
              <div>
                <p className="font-medium text-gray-900">{profile.email}</p>
                <p className="text-sm text-gray-500">Role: {getRoleDisplayName(profile.role)}</p>
                <p className="text-sm text-gray-500">Status: {profile.ativo ? 'Ativo' : 'Inativo'}</p>
              </div>
            </div>
          </div>

          {/* Dados Básicos — vendedor não pode alterar; só vê a seção Alterar Senha */}
          {!isVendedor && (
          <div className="space-y-4">
            <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
              <User className="h-5 w-5 text-blue-600" />
              Dados Básicos
            </h3>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="nome" className="text-sm font-medium">
                  Nome Completo *
                </Label>
                <Input
                  id="nome"
                  value={formData.nome}
                  onChange={(e) => setFormData({ ...formData, nome: e.target.value })}
                  placeholder="Digite seu nome completo"
                  required
                  className="mt-1"
                />
              </div>
              
              <div>
                <Label htmlFor="email" className="text-sm font-medium">
                  Email *
                </Label>
                <Input
                  id="email"
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  placeholder="seu@email.com"
                  required
                  className="mt-1"
                />
              </div>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="telefone" className="text-sm font-medium">
                  Telefone
                </Label>
                <Input
                  id="telefone"
                  value={formData.telefone}
                  onChange={(e) => setFormData({ ...formData, telefone: e.target.value })}
                  placeholder="(11) 99999-9999"
                  className="mt-1"
                />
              </div>
              
              <div>
                <Label htmlFor="setor" className="text-sm font-medium">
                  Setor/Departamento
                </Label>
                <Select 
                  value={formData.setor_departamento || ""} 
                  onValueChange={(value: "vendas" | "financeiro" | "tecnico" | "administrativo") => 
                    setFormData({ ...formData, setor_departamento: value || undefined })
                  }
                >
                  <SelectTrigger className="mt-1">
                    <SelectValue placeholder="Selecione um setor" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="vendas">Vendas</SelectItem>
                    <SelectItem value="financeiro">Financeiro</SelectItem>
                    <SelectItem value="tecnico">Técnico</SelectItem>
                    <SelectItem value="administrativo">Administrativo</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>
          )}

          {/* Alteração de Senha */}
          <div className="space-y-4">
            <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
              <Lock className="h-5 w-5 text-orange-600" />
              Alterar Senha
            </h3>
            <p className="text-sm text-gray-600">
              Preencha apenas se desejar alterar sua senha atual
            </p>
            
            <div className="space-y-4">
              <div>
                <Label htmlFor="senhaAtual" className="text-sm font-medium">
                  Senha Atual
                </Label>
                <div className="relative">
                  <Input
                    id="senhaAtual"
                    type={showCurrentPassword ? "text" : "password"}
                    value={formData.senhaAtual}
                    onChange={(e) => setFormData({ ...formData, senhaAtual: e.target.value })}
                    placeholder="Digite sua senha atual"
                    className="mt-1 pr-10"
                  />
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    className="absolute right-0 top-0 h-full px-3 py-2 hover:bg-transparent"
                    onClick={() => setShowCurrentPassword(!showCurrentPassword)}
                  >
                    {showCurrentPassword ? (
                      <EyeOff className="h-4 w-4" />
                    ) : (
                      <Eye className="h-4 w-4" />
                    )}
                  </Button>
                </div>
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="novaSenha" className="text-sm font-medium">
                    Nova Senha
                  </Label>
                  <div className="relative">
                    <Input
                      id="novaSenha"
                      type={showNewPassword ? "text" : "password"}
                      value={formData.novaSenha}
                      onChange={(e) => setFormData({ ...formData, novaSenha: e.target.value })}
                      placeholder="8+ caracteres, maiúscula, minúscula, número e símbolo"
                      className="mt-1 pr-10"
                    />
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      className="absolute right-0 top-0 h-full px-3 py-2 hover:bg-transparent"
                      onClick={() => setShowNewPassword(!showNewPassword)}
                    >
                      {showNewPassword ? (
                        <EyeOff className="h-4 w-4" />
                      ) : (
                        <Eye className="h-4 w-4" />
                      )}
                    </Button>
                  </div>
                </div>
                
                <div>
                  <Label htmlFor="confirmarSenha" className="text-sm font-medium">
                    Confirmar Nova Senha
                  </Label>
                  <div className="relative">
                    <Input
                      id="confirmarSenha"
                      type={showConfirmPassword ? "text" : "password"}
                      value={formData.confirmarSenha}
                      onChange={(e) => setFormData({ ...formData, confirmarSenha: e.target.value })}
                      placeholder="Confirme a nova senha"
                      className="mt-1 pr-10"
                    />
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      className="absolute right-0 top-0 h-full px-3 py-2 hover:bg-transparent"
                      onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                    >
                      {showConfirmPassword ? (
                        <EyeOff className="h-4 w-4" />
                      ) : (
                        <Eye className="h-4 w-4" />
                      )}
                    </Button>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Configurações de Acesso */}
          {isAdmin && (
            <div className="space-y-4">
              <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
                <Shield className="h-5 w-5 text-purple-600" />
                Configurações de Acesso
              </h3>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="role" className="text-sm font-medium">
                    Nível de Acesso
                  </Label>
                  <Select 
                    value={formData.role || ""} 
                    onValueChange={(value: UserRole) => 
                      setFormData({ ...formData, role: value })
                    }
                  >
                    <SelectTrigger className="mt-1">
                      <SelectValue placeholder="Selecione o nível de acesso" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="admin">{getRoleDisplayName('admin')}</SelectItem>
                      <SelectItem value="operator">{getRoleDisplayName('operator')}</SelectItem>
                      <SelectItem value="viewer">{getRoleDisplayName('viewer')}</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                
                <div className="flex items-center space-x-2 pt-6">
                  <Switch
                    id="ativo"
                    checked={formData.ativo}
                    onCheckedChange={(checked) => setFormData({ ...formData, ativo: checked })}
                  />
                  <Label htmlFor="ativo" className="text-sm font-medium">
                    Usuário Ativo
                  </Label>
                </div>
              </div>
            </div>
          )}

          {/* Botões de Ação */}
          <div className="flex justify-end space-x-3 pt-6 border-t">
            <Button
              type="button"
              variant="outline"
              onClick={handleReset}
              disabled={updatePerfil.isPending || updateUsuario.isPending || isUpdatingPassword}
              className="min-w-[100px]"
            >
              Resetar
            </Button>
            <Button
              type="button"
              variant="outline"
              onClick={() => setOpen(false)}
              disabled={updatePerfil.isPending || updateUsuario.isPending || isUpdatingPassword}
              className="min-w-[100px]"
            >
              Cancelar
            </Button>
            <Button 
              type="submit" 
              disabled={updatePerfil.isPending || updateUsuario.isPending || isUpdatingPassword}
              className="min-w-[100px] bg-blue-600 hover:bg-blue-700"
            >
              {(updatePerfil.isPending || updateUsuario.isPending || isUpdatingPassword) ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Salvando...
                </>
              ) : (
                <>
                  <CheckCircle className="mr-2 h-4 w-4" />
                  Salvar
                </>
              )}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
} 